import React, { useState, useEffect } from 'react';
import { FaPlay, FaEye, FaYoutube, FaBroadcastTower, FaUsers } from 'react-icons/fa';
import AOS from 'aos';
import 'aos/dist/aos.css';

const YouTubeLiveStreaming = () => {
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 1000 });
    fetchLiveStatus();
    
    // Refresh data every 30 seconds
    const interval = setInterval(fetchLiveStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchLiveStatus = async () => {
    try {
      const response = await fetch('https://freebillingapp.xyz/api/live/status');
      const data = await response.json();
      
      if (data.success) {
        setLiveData(data);
        setError(null);
      } else {
        setError('Failed to fetch live status');
      }
    } catch (err) {
      setError('Error connecting to live streaming service');
      console.error('Live status fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatViewerCount = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live':
        return 'text-red-500';
      case 'offline':
        return 'text-gray-500';
      default:
        return 'text-yellow-500';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'live':
        return 'bg-red-100 border-red-200';
      case 'offline':
        return 'bg-gray-100 border-gray-200';
      default:
        return 'bg-yellow-100 border-yellow-200';
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading live streaming status...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100 rounded-full opacity-20 blur-3xl -translate-y-32 translate-x-32"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-red-100 rounded-full opacity-20 blur-3xl translate-y-24 -translate-x-24"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12" data-aos="fade-down">
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <FaBroadcastTower className="animate-pulse" />
            LIVE STREAMING
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Watch Our <span className="text-red-600">Live</span> Content
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Stay connected with our live streaming content and real-time updates from IINSAF
          </p>
        </div>

        {/* Live Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-aos="fade-up">
          {/* Total Viewers Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Viewers</p>
                <p className="text-3xl font-bold text-blue-600">
                  {liveData?.total_viewers ? formatViewerCount(liveData.total_viewers) : '0'}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <FaUsers className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>

          {/* Live Channels Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Live Channels</p>
                <p className="text-3xl font-bold text-green-600">
                  {liveData?.total_channels || 0}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <FaYoutube className="text-green-600 text-xl" />
              </div>
            </div>
          </div>

          {/* Live Status Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Status</p>
                <p className={`text-2xl font-bold ${liveData?.is_live ? 'text-red-600' : 'text-gray-500'}`}>
                  {liveData?.is_live ? 'LIVE' : 'OFFLINE'}
                </p>
              </div>
              <div className={`p-3 rounded-full ${liveData?.is_live ? 'bg-red-100' : 'bg-gray-100'}`}>
                <FaBroadcastTower className={`text-xl ${liveData?.is_live ? 'text-red-600 animate-pulse' : 'text-gray-500'}`} />
              </div>
            </div>
          </div>
        </div>

        {/* Live Channels List */}
        {liveData?.live_channels && liveData.live_channels.length > 0 ? (
          <div className="space-y-6" data-aos="fade-up" data-aos-delay="200">
            <h3 className="text-2xl font-bold text-gray-800 text-center mb-8">
              Currently Live Channels
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {liveData.live_channels.map((channel, index) => (
                <div
                  key={channel.channel_id}
                  className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 ${getStatusBgColor(channel.status)} hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
                  data-aos="zoom-in"
                  data-aos-delay={index * 100}
                >
                  {/* Channel Header */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                          <FaYoutube className="text-red-600 text-xl" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800 text-lg line-clamp-2">
                            {channel.title}
                          </h4>
                          <p className="text-gray-600 text-sm">Channel ID: {channel.channel_id}</p>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(channel.status)}`}>
                        {channel.status.toUpperCase()}
                      </div>
                    </div>

                    {/* Viewer Count */}
                    <div className="flex items-center gap-2 mb-4">
                      <FaEye className="text-gray-500" />
                      <span className="text-gray-700 font-medium">
                        {formatViewerCount(channel.viewers)} viewers
                      </span>
                    </div>

                    {/* Watch Button */}
                    <a
                      href={channel.live_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors duration-300"
                    >
                      <FaPlay className="text-sm" />
                      Watch Live
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12" data-aos="fade-up">
            <div className="bg-white rounded-2xl shadow-lg p-12 max-w-md mx-auto">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaBroadcastTower className="text-gray-400 text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">No Live Streams</h3>
              <p className="text-gray-600 mb-6">
                There are currently no live streams. Check back later for live content!
              </p>
              <button
                onClick={fetchLiveStatus}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-300"
              >
                Refresh Status
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12" data-aos="fade-up">
            <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md mx-auto">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaBroadcastTower className="text-red-500 text-2xl" />
              </div>
              <h3 className="text-lg font-bold text-red-800 mb-2">Connection Error</h3>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchLiveStatus}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-300"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Footer CTA */}
        <div className="text-center mt-12" data-aos="fade-up" data-aos-delay="300">
          <div className="bg-gradient-to-r from-blue-600 to-red-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Stay Updated with IINSAF</h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Subscribe to our YouTube channel to get notified about live streams, news updates, and exclusive content.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.youtube.com/@iinsafofficial"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-xl transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <FaYoutube className="text-xl" />
                Subscribe on YouTube
              </a>
              <button
                onClick={fetchLiveStatus}
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold py-3 px-8 rounded-xl transition-all duration-300"
              >
                Refresh Live Status
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default YouTubeLiveStreaming;
